/**
 * SOLID Review
 * S — Strong. One concern: the websocket's health — the state the table is told, the zombie-socket detector, who the socket
 *     says it is, and the recovery when the player's attention comes back.
 * O — Good. A new way back to the page is one more listener; a new threshold, one constant.
 * L — N/A — no inheritance hierarchy.
 * I — Good. The state (`getState`, `onStateChange`), the two ways to re-open the socket (`forceReconnect`, `reidentify`), and
 *     the graphql-ws client it configured (`client`), which the Apollo client's websocket link carries.
 * D — Strong. The token store, the page's events, the timers, the clock and graphql-ws's `createClient` are handed in.
 */
import type { Client, ClientOptions } from 'graphql-ws';
import { PROD_GRAPHQL_WS_URL } from '../../shared/appConfig';
import type { IEntityResume, SessionTokenStore } from '../storage/SessionTokenStore';
import type { IServiceWindowEvents } from '../platform/IServiceWindowEvents';
import type { IServiceTimers, TTimer } from '../platform/IServiceTimers';
import type { IServiceClock } from '../platform/IServiceClock';

export type WsConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'failed';

/** The open socket, as the zombie detector watches it. */
type TSocket = Pick<WebSocket, 'readyState' | 'close'>;
/** `WebSocket.OPEN`. */
const OPEN = 1;
const PING_ACK_TIMEOUT_MS = 5_000;
/**
 * `focus` fires far more often than a real return (every tap back into the window), so a socket that is down is kicked only
 * after a meaningful absence — a quick blur/focus flicker leaves it to graphql-ws's own retry.
 */
const AWAY_RECONNECT_THRESHOLD_MS = 2_000;
/**
 * Only force-cycle a socket that is ALREADY 'connected' after a long absence — a short focus flick must NOT terminate a healthy
 * connection. On a single device with several split-screen panes the OS already churns the sockets (freeze/wake); terminating
 * healthy ones on every focus on top of that storms the backend's per-player disconnect-grace timers (each reconnect cancels the
 * grace), so a genuinely-gone player is never stably suspended and the table stalls. A real half-open zombie is still caught by
 * the keep-alive ping/ack timeout (10 s + 5 s).
 */
const ZOMBIE_RECONNECT_THRESHOLD_MS = 30_000;

/** What the monitor is built from — the composition root hands the browser's, a test its own. */
export interface IConfigDepWsConnectionMonitor {
  /** Where the socket connects (`WsConnectionMonitor.urlFor`). */
  readonly url: string;
  /** The tab's token and stored game: what the socket introduces itself with. */
  readonly tokens: SessionTokenStore;
  readonly page: IServiceWindowEvents;
  readonly timers: IServiceTimers;
  readonly clock: IServiceClock;
  /** graphql-ws's `createClient`. */
  readonly createClient: (options: ClientOptions) => Client;
}

/**
 * THE WEBSOCKET'S HEALTH (frontend-infrastructure-oo P2, 2026-10-28, §28.287) — `wsConnectionMonitor.ts`'s module variables as an
 * object's fields and its exported functions as methods, built ONCE by the composition root. Behaviour unchanged: the monitor's
 * corpus (`test/infrastructure/golden/monitorCorpus.test.ts`) replays every state, socket and introduction it pinned.
 *
 * Mobile context: when a player switches to another app the OS freezes this tab's JS and timers and often suspends the TCP
 * socket. The socket can be left half-open — the server already dropped its side (5 s ws heartbeat) but the browser still reports
 * it OPEN, so graphql-ws keeps the state 'connected' and silently delivers no game events until a TCP timeout, which can be
 * minutes. Two mechanisms collapse that window:
 *   1. keep-alive ping + ack timeout → close(4408): the steady-state zombie detector (worst case ~15 s), per graphql-ws's recipe;
 *   2. return-from-away → `forceReconnect()`: when the player's attention comes back, the (possibly dead) socket is terminated so
 *      graphql-ws reconnects at once. The reconnect re-sends playerId and roomId, and the backend's onConnect cancels the
 *      disconnect grace timer (or resumes the player if already suspended). A return is detected across ALL the ways it happens
 *      on mobile — a full app switch (`visibilitychange`), an Android split-screen focus move, where both panes stay visible
 *      and only `focus`/`blur` fire, back/forward from the cache (`pageshow`), the network restored (`online`).
 */
export class WsConnectionMonitor {
  /** The graphql-ws client this monitor configured — lazy: it opens with the first subscription. */
  readonly client: Client;
  private state: WsConnectionState = 'disconnected';
  private readonly listeners = new Set<(s: WsConnectionState) => void>();
  /** The live socket and the outstanding ping-ack timer the zombie detector keeps. */
  private activeSocket: TSocket | null = null;
  /**
   * Who the OPEN socket introduced itself as (`playerId:roomId`), and who the one being opened will — '' for an anonymous socket.
   * The server learns the seat ONLY from `connection_init` (GraphQLTransport.onWsConnect); see `reidentify`.
   */
  private identity = '';
  private pendingIdentity = '';
  private pingAckTimeout: TTimer | null = null;
  /** When the player's attention went, 0 while it is here. */
  private awaySince = 0;

  constructor(private readonly deps: IConfigDepWsConnectionMonitor) {
    const options: ClientOptions = {
      url: deps.url,
      lazy: true,
      connectionParams: () => {
        const token = deps.tokens.token();
        const session = deps.tokens.resume();
        this.pendingIdentity = this.identityOf(session);
        return {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(session ? { playerId: session.playerId, roomId: session.roomId } : {}),
        };
      },
      // Send a client ping every 10 s; if the server doesn't pong within 5 s, the socket is dead — close it so the retry
      // logic below reconnects.
      keepAlive: 10_000,
      // Retry forever (a mobile network can flap for a while). Without this the client would give up after the default
      // attempts and need a manual refresh.
      retryAttempts: Infinity,
      shouldRetry: () => true,
      // Capped exponential backoff so a long outage doesn't hammer the server.
      retryWait: async (retries: number) => {
        const delay = Math.min(1_000 * 2 ** retries, 30_000);
        await new Promise<void>((resolve) => { deps.timers.setTimeout(resolve, delay); });
      },
      on: {
        connecting: () => this.setState(this.state === 'disconnected' ? 'connecting' : 'reconnecting'),
        connected: (socket) => {
          this.activeSocket = socket as TSocket;
          this.identity = this.pendingIdentity;
          this.setState('connected');
          // A seat saved while this socket was being opened (after its params were read) is announced now.
          this.reidentify();
        },
        // `received === false` ⇒ we just SENT a keep-alive ping; arm the ack timer.
        ping: (received) => {
          if (!received) {
            this.pingAckTimeout = deps.timers.setTimeout(() => {
              if (this.activeSocket && this.activeSocket.readyState === OPEN) {
                this.activeSocket.close(4408, 'Request Timeout');
              }
            }, PING_ACK_TIMEOUT_MS);
          }
        },
        // `received === true` ⇒ the server answered our ping; the socket is alive.
        pong: (received) => {
          if (received && this.pingAckTimeout !== null) {
            deps.timers.clearTimeout(this.pingAckTimeout);
            this.pingAckTimeout = null;
          }
        },
        closed: () => {
          this.activeSocket = null;
          if (this.pingAckTimeout !== null) {
            deps.timers.clearTimeout(this.pingAckTimeout);
            this.pingAckTimeout = null;
          }
          this.setState('disconnected');
        },
        error: () => this.setState('failed'),
      },
    };
    this.client = deps.createClient(options);
    deps.page.on('visibilitychange', () => {
      if (deps.page.isVisible()) this.reconnectIfReturned();
      else this.markAway();
    });
    deps.page.on('blur', () => this.markAway());
    deps.page.on('focus', () => this.reconnectIfReturned());
    deps.page.on('pageshow', () => this.reconnectIfReturned(true));
    deps.page.on('online', () => this.reconnectIfReturned(true));
  }

  /**
   * Where the socket connects: an explicit `VITE_GRAPHQL_WS_URL` (a non-empty string), else in development the page's own host
   * (`wss:` under https), else production's. Read by the composition root, which reads the environment.
   */
  static urlFor(env: Record<string, unknown>, location: { protocol: string; host: string }): string {
    const explicit = env['VITE_GRAPHQL_WS_URL'];
    if (typeof explicit === 'string' && explicit !== '') return explicit;
    const isDev = env['DEV'] === 'true' || env['DEV'] === true;
    if (!isDev) return PROD_GRAPHQL_WS_URL;
    return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/graphql`;
  }

  getState(): WsConnectionState {
    return this.state;
  }

  onStateChange(handler: (s: WsConnectionState) => void): () => void {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  /**
   * Abruptly drop the current socket so graphql-ws reconnects on the next tick. Safe when idle (a lazy client with no active
   * subscriptions stays closed) and when connected (a fresh, immediately-reconnecting socket). Used on foreground-resume and
   * network-restore to recover a half-open socket without waiting for the keep-alive ack timeout or a TCP-level timeout.
   */
  forceReconnect(): void {
    try {
      this.client.terminate();
    } catch {
      /* terminate on an already-dead/never-opened socket is a no-op */
    }
  }

  /**
   * RE-INTRODUCE THE SOCKET AS THE SEATED PLAYER (2026-10-07, frontend strict-ports FE9, §28.206). The server tracks a player's
   * presence — the disconnect grace that suspends a player who has gone, and the resume when they come back — ONLY for a socket
   * whose `connection_init` named `playerId` and `roomId`; any other socket is anonymous to it for its whole life. The client is
   * lazy, and since the server pushes the account's balance (backend §28.190) the first subscription starts as soon as there is
   * a player — on the welcome screen, BEFORE any seat is stored. So when the tab stores a seat (`ClientTabStorage.persist`), the
   * socket is re-opened IF it introduced itself as anyone else — graphql-ws re-reads the params and re-subscribes every stream.
   * A seat equal to the socket's own reconnects nothing; a cleared seat (leaving) reconnects nothing either.
   */
  reidentify(): void {
    if (this.activeSocket === null) return;
    const wanted = this.identityOf(this.deps.tokens.resume());
    if (wanted === '' || wanted === this.identity) return;
    this.forceReconnect();
  }

  private setState(s: WsConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    this.listeners.forEach((fn) => fn(s));
  }

  private identityOf(session: IEntityResume | null): string {
    return session === null ? '' : `${session.playerId}:${session.roomId}`;
  }

  private markAway(): void {
    // Idempotent: the first loss of attention wins, so paired events (blur AND visibilitychange:hidden on a full app switch)
    // record a single away-start.
    if (this.awaySince === 0) this.awaySince = this.deps.clock.now();
  }

  /** Reconnect only when the socket actually needs it — never churn a healthy one. */
  private reconnectIfReturned(always = false): void {
    const awayMs = this.awaySince === 0 ? 0 : this.deps.clock.now() - this.awaySince;
    this.awaySince = 0;
    if (always) { this.forceReconnect(); return; } // pageshow / online
    if (this.state !== 'connected') {
      if (awayMs >= AWAY_RECONNECT_THRESHOLD_MS) this.forceReconnect(); // genuinely down → kick it
      return;
    }
    if (awayMs >= ZOMBIE_RECONNECT_THRESHOLD_MS) this.forceReconnect(); // connected but long away → clear a possible zombie
  }
}
